import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { PlanSlug } from '../lib/subscriptions';

export type ViewAsRole = 'admin' | 'free' | 'explorer' | 'sensei' | 'concierge' | 'agent-50' | 'agent-200' | 'agent-unlimited';

interface ImpersonateCtx {
  viewAsRole: ViewAsRole;
  isImpersonating: boolean;
  setViewAsRole: (role: ViewAsRole) => void;
  resetToAdmin: () => void;
  /** Use this instead of AuthContext.isAdmin for UI rendering decisions */
  effectiveIsAdmin: boolean;
  /** Use this instead of subscription.planSlug for UI rendering decisions */
  effectivePlanSlug: PlanSlug;
}

const Ctx = createContext<ImpersonateCtx>({
  viewAsRole: 'admin',
  isImpersonating: false,
  setViewAsRole: () => {},
  resetToAdmin: () => {},
  effectiveIsAdmin: true,
  effectivePlanSlug: 'concierge',
});

export function ImpersonateProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole>('admin');

  const isImpersonating = isAdmin && viewAsRole !== 'admin';

  const setViewAsRole = useCallback((role: ViewAsRole) => {
    if (!isAdmin) return;
    setViewAsRoleState(role);
  }, [isAdmin]);

  const resetToAdmin = useCallback(() => {
    setViewAsRoleState('admin');
  }, []);

  const effectiveIsAdmin = isAdmin && viewAsRole === 'admin';
  const effectivePlanSlug: PlanSlug = viewAsRole === 'admin' ? 'concierge' : viewAsRole;

  return (
    <Ctx.Provider value={{
      viewAsRole,
      isImpersonating,
      setViewAsRole,
      resetToAdmin,
      effectiveIsAdmin,
      effectivePlanSlug,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useImpersonate = () => useContext(Ctx);
